import { z } from 'zod';

import { normalizarPlaca } from '../texto';

/**
 * Esquemas del flujo de accesos que usa el **kiosco** de entrada.
 *
 * El kiosco valida un QR (el `token` de una `Invitacion`), registra el `Acceso`
 * con los datos del visitante y sube las fotos (rostro e identificación). El
 * mismo contrato lo entiende el API (para validar) y la app del kiosco (para no
 * mandar basura). Los enums se declaran a mano —como en el resto de `shared`—
 * porque este paquete también lo carga el navegador y no puede importar Prisma;
 * hay pruebas del lado del API que verifican que no se separen del schema.
 */

// ── Enums espejo del schema ───────────────────────────────────

/**
 * Lo más largo que puede medir un token de QR.
 *
 * **No es una restricción de diseño: es el guardia que impide usar el cuerpo de
 * la petición como vertedero.** Por eso vale más que el token más largo que
 * este sistema puede emitir, y no al revés — que el 512 de antes decidiera si
 * un QR propio servía era justo el fallo: con un nombre largo salía un token de
 * 681 y la reja lo rechazaba **por largo, antes de mirarle la firma**. El
 * residente lo acababa de generar y el visitante se quedaba en la puerta con un
 * «los datos enviados no son válidos» que no explica nada.
 *
 * La cuenta, medida y no estimada: el envoltorio fijo son 90 caracteres —`v1.`,
 * el punto, y la firma Ed25519 de 64 bytes en base64url sin relleno—. El techo
 * real hoy es 601: nombre en su tope de 60 caracteres con letras de tres bytes,
 * sobre un slug de 40 y una invitación recurrente con los siete días. Los 167
 * de holgura son para que quepa un campo más sin repetir esta cuenta.
 */
export const TOKEN_QR_MAX = 768;

export const MODOS_ACCESO = ['VEHICULAR', 'PEATONAL'] as const;
export type ModoAcceso = (typeof MODOS_ACCESO)[number];

export const TIPOS_FOTO = ['ROSTRO', 'IDENTIFICACION', 'VEHICULO'] as const;
export type TipoFoto = (typeof TIPOS_FOTO)[number];

// ── Validar QR ────────────────────────────────────────────────

export const validarQrSchema = z.object({
  /** El contenido crudo del QR = `Invitacion.token`. */
  token: z.string().trim().min(1, 'El QR viene vacío').max(TOKEN_QR_MAX),
});
export type ValidarQr = z.infer<typeof validarQrSchema>;

/**
 * Respuesta de validar-qr: lo justo para que el kiosco arme la pantalla de
 * confirmación sin exponer datos de más del residente (sus teléfonos y nombres
 * no viajan; solo la unidad y qué exige el flujo).
 */
export interface InvitacionValidada {
  invitacionId: string;
  /** Clave corta de la unidad que invita: "A-101", "Casa 24". */
  unidad: string;
  /** Nombre que puso el residente al invitar, si lo puso. Se puede corregir. */
  nombreInvitado: string | null;
  tipoVisita: { id: string; nombre: string; color: string } | null;
  /** Qué pasos exige el tipo de visita; el kiosco arma el flujo con esto. */
  requiere: {
    fotoRostro: boolean;
    fotoIdentificacion: boolean;
    autorizacion: boolean;
  };
  vigenteHasta: string;
  usosRestantes: number;
}

// ── Crear acceso ──────────────────────────────────────────────

export const crearAccesoKioscoSchema = z.object({
  /**
   * Token del QR. El servidor lo **revalida** al crear el acceso (no confía en
   * que el kiosco ya lo validó): así una invitación no se puede usar dos veces
   * ni después de expirar, aunque el kiosco tuviera datos viejos.
   */
  token: z.string().trim().min(1).max(TOKEN_QR_MAX),
  /**
   * Punto de acceso donde está el kiosco. Se puede omitir: el servidor lo
   * deriva del dispositivo/cuenta del kiosco si no viaja.
   */
  puntoAccesoId: z.string().cuid().optional(),
  /**
   * **Qué kiosco es.** Su identificador en el bus, el mismo del tópico.
   *
   * De él sale el carril, y por tanto la pluma que se abre. Un acceso con más de
   * un carril de visitas lleva un FastPass en cada uno: sin saber cuál mandó la
   * visita se abría el de menor orden —siempre el mismo, siempre el equivocado
   * para el otro— y la pantalla del guardia mezclaba los dos.
   *
   * Opcional, para no dejar tirado a un kiosco que todavía no se actualiza: sin
   * él se cae al comportamiento anterior.
   */
  clienteId: z.string().trim().max(64).optional(),
  tipoVisitaId: z.string().cuid().optional(),
  modo: z.enum(MODOS_ACCESO).default('PEATONAL'),

  // Datos del visitante, ya confirmados/corregidos en la pantalla táctil tras
  // el OCR. Todos opcionales: una invitación válida basta para entrar; los datos
  // enriquecen el registro pero no lo bloquean.
  nombreVisitante: z.string().trim().max(120).optional(),
  documento: z.string().trim().max(60).optional(),
  telefono: z.string().trim().max(30).optional(),
  placa: z.string().trim().min(1).max(15).transform(normalizarPlaca).optional(),
  colorVehiculo: z.string().trim().max(30).optional(),
  marcaVehiculo: z.string().trim().max(40).optional(),
  acompanantes: z.number().int().min(0).max(20).default(0),
  observaciones: z.string().trim().max(500).optional(),
});
export type CrearAccesoKiosco = z.infer<typeof crearAccesoKioscoSchema>;

export interface AccesoCreado {
  accesoId: string;
  estado: 'DENTRO' | 'PENDIENTE_AUTORIZACION';
  /** Qué fotos faltan por subir según el tipo de visita. */
  fotosPendientes: TipoFoto[];
}

// ── Camino sin QR: directorio de viviendas ────────────────────

/**
 * Lo que el kiosco recibe para armar el directorio.
 *
 * **Solo claves.** Nunca nombres de residentes ni teléfonos: la pantalla está en
 * la calle y cualquiera puede tocarla (docs/09 §2).
 */
export interface LetraDirectorio {
  /** Letra de la vivienda: A–Z. */
  letra: string;
  viviendas: { id: string; clave: string; numero: string }[];
}

export const crearAccesoSinQrSchema = z.object({
  /** Vivienda que el visitante eligió en el directorio. */
  unidadId: z.string().cuid(),
  puntoAccesoId: z.string().cuid().optional(),
  /**
   * **Qué kiosco es.** Su identificador en el bus, el mismo del tópico.
   *
   * De él sale el carril, y por tanto la pluma que se abre. Un acceso con más de
   * un carril de visitas lleva un FastPass en cada uno: sin saber cuál mandó la
   * visita se abría el de menor orden —siempre el mismo, siempre el equivocado
   * para el otro— y la pantalla del guardia mezclaba los dos.
   *
   * Opcional, para no dejar tirado a un kiosco que todavía no se actualiza: sin
   * él se cae al comportamiento anterior.
   */
  clienteId: z.string().trim().max(64).optional(),
  tipoVisitaId: z.string().cuid().optional(),
  modo: z.enum(MODOS_ACCESO).default('PEATONAL'),
  /** Lo que saque el OCR de la identificación; el visitante no teclea nada. */
  nombreVisitante: z.string().trim().max(120).optional(),
});
export type CrearAccesoSinQr = z.infer<typeof crearAccesoSinQrSchema>;

// ── Subir foto ────────────────────────────────────────────────

/**
 * La imagen viaja como multipart (campo `archivo`); este esquema valida los
 * campos de texto que la acompañan.
 */
/**
 * Con la foto viajan, opcionalmente, unas métricas de calidad que el kiosco
 * calculó sobre el cuadro crudo. Son números, no datos personales, y sirven
 * para explicar después por qué una credencial se leyó o no. Van como texto
 * porque el cuerpo es multipart: ahí todo llega como cadena.
 */
const numeroDeTexto = z.coerce.number().finite().optional();

export const subirFotoSchema = z.object({
  tipo: z.enum(TIPOS_FOTO),
  nitidez: numeroDeTexto,
  reflejo: numeroDeTexto,
  reflejoLocal: numeroDeTexto,
  luminancia: numeroDeTexto,
  ladoLargoPx: numeroDeTexto,
});
export type SubirFoto = z.infer<typeof subirFotoSchema>;

/**
 * El guardia corrige el nombre que sacó el OCR.
 *
 * Existe porque leer una credencial fotografiada en la calle no es fiable, y el
 * nombre acaba en la bitácora del condominio. Quien lo tiene delante en papel
 * puede arreglarlo en dos segundos; nadie más va a poder después.
 */
export const corregirNombreSchema = z.object({
  nombreVisitante: z.string().trim().min(2).max(120),
});
export type CorregirNombre = z.infer<typeof corregirNombreSchema>;

// ── El guardia dirige la captura de la credencial ─────────────

/**
 * Lo que el guardia le dice al kiosco mientras el visitante acomoda su
 * credencial en la charola.
 *
 * Antes el kiosco disparaba solo en cuanto detectaba algo con forma de tarjeta,
 * y el guardia solo podía aprobar o rechazar lo que saliera. Era fácil acabar
 * con la credencial al revés, cortada, o con un papel cualquiera: la máquina no
 * sabe distinguirlo y la persona que sí sabe llegaba tarde.
 *
 * Ahora el guardia mira en vivo y corrige **antes** de que se tome la foto.
 */
export const INSTRUCCIONES_KIOSCO = [
  /** Tómala ya: así está bien. */
  'CAPTURAR',
  /** La puso boca abajo o del revés. */
  'VOLTEAR',
  /** Eso no es una identificación. */
  'NO_ES_CREDENCIAL',
  /** La foto salió mal; que la vuelva a acomodar. */
  'REPETIR',
] as const;
export type InstruccionKiosco = (typeof INSTRUCCIONES_KIOSCO)[number];

export const instruirKioscoSchema = z.object({
  instruccion: z.enum(INSTRUCCIONES_KIOSCO),
});
export type InstruirKiosco = z.infer<typeof instruirKioscoSchema>;

/**
 * El guardia da por buena la credencial capturada y el nombre que salió de
 * ella.
 *
 * Es un paso aparte de autorizar la entrada a propósito: son dos juicios
 * distintos. «La credencial se leyó bien» lo decide mirando el papel; «esta
 * persona pasa» lo decide mirando a la persona y su destino.
 */
export const aceptarCredencialSchema = z.object({
  nombreVisitante: z.string().trim().min(2).max(120),
});
export type AceptarCredencial = z.infer<typeof aceptarCredencialSchema>;

/** Lo que el kiosco recibe en cada sondeo mientras dura la captura. */
export interface EstadoCaptura {
  accesoId: string;
  estado: string;
  recapturar: boolean;
  instruccion: InstruccionKiosco | null;
  /** Hace de número de versión: el kiosco solo redibuja cuando cambia. */
  instruccionEn: string | null;
}

// ── Señalización WebRTC ───────────────────────────────────────

/**
 * Un descriptor SDP (oferta o respuesta) viajando por el buzón del API.
 *
 * El API no lo interpreta: es una cadena opaca que el guardia y el kiosco se
 * pasan para montar la conexión de video directa entre ellos. Se valida solo el
 * tamaño, para que nadie use el buzón como vertedero.
 */
export const senalWebrtcSchema = z.object({
  sdp: z
    .string()
    .min(1)
    .max(64 * 1024),
});
export type SenalWebrtc = z.infer<typeof senalWebrtcSchema>;
